// 主（あるじ）Lv。上限は本編クリアまで1000（CONFIG.level.maxBeforeClear）、クリア後 CONFIG.level.max。上限以降のEXPは霊玉に換える（EXP倍率の上乗せ分は除く・1日の上限あり）。
import { CONFIG, configVersion } from './config';
import { stageKey, type Profile, type Resources } from './profile';

export function expToNext(level: number): number {
  const c = CONFIG.level;
  const u = c.unit ?? 1;
  // 旧Lv（上限100のとき）の位置。旧Lv1つ分（主Lv u 個）の真ん中で旧式を引く
  const x = (level + (u - 1) / 2) / u;
  // 終盤のLvは必要量を相対的に軽く（taper：from で1倍 → to で mult 倍の直線。BALANCE.md 17章）
  const t = c.taper;
  const k = t && t.to > t.from ? 1 + (t.mult - 1) * Math.max(0, Math.min(1, (x - t.from) / (t.to - t.from))) : 1;
  const after = c.maxBeforeClear && level >= c.maxBeforeClear ? (c.afterClearNeedMult ?? 1) : 1;
  return Math.max(1, Math.round(((c.needA * Math.pow(x, c.needP) + (c.needC ?? 0) * x + c.needB) * k * valleyMult(x) * after) / u));
}

/** 旧Lv（上限100のとき）に直した段：主Lv unit 個ごとに1つ上がる（解放力・兵糧の最大値に使う） */
export const levelTier = (level: number) => Math.floor((Math.max(1, level) + (CONFIG.level.unit ?? 1) - 1) / (CONFIG.level.unit ?? 1));

/** 中盤の谷をならす倍率（CONFIG.level.valley の折れ線。旧Lv x で引く。空・範囲外は1） */
function valleyMult(x: number): number {
  const pts = CONFIG.level.valley;
  if (!pts?.length) return 1;
  if (x <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / Math.max(1e-9, x1 - x0);
  }
  return pts[pts.length - 1][1];
}

let cache = { ver: -1, totals: [] as number[] };
/** totals[L] = Lv L になるまでの累計EXP（totals[1] = 0） */
function totals(): number[] {
  if (cache.ver === configVersion()) return cache.totals;
  const t = [0, 0];
  for (let L = 1; L < CONFIG.level.max; L++) t.push(t[L] + expToNext(L));
  cache = { ver: configVersion(), totals: t };
  return t;
}

/** その人の主Lvの上限：本編（第100陣）をクリアするまでは maxBeforeClear */
export const levelCapOf = (p: Pick<Profile, 'progress'>) => ((p.progress[stageKey(100)]?.clears ?? 0) > 0 ? CONFIG.level.max : (CONFIG.level.maxBeforeClear ?? CONFIG.level.max));

export const expForLevel = (level: number) => totals()[Math.max(1, Math.min(CONFIG.level.max, level))];

/** max：その人の上限（levelCapOf）。省くと全体の上限 */
export function levelInfo(exp: number, max = CONFIG.level.max): { level: number; into: number; need: number; total: number } {
  const t = totals();
  let level = 1;
  // 二分探索
  let lo = 1,
    hi = max;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (t[mid] <= exp) {
      level = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  if (level >= max) return { level: max, into: exp - t[max], need: 0, total: exp };
  return { level, into: exp - t[level], need: t[level + 1] - t[level], total: exp };
}

/**
 * 主EXPを加える。baseExp は倍率を掛ける前の量（霊玉換算用）、exp は倍率込みの量。
 * 上限Lvに達している分の baseExp は霊玉に換算（1日の上限あり）。Lvアップで兵糧は回復しない（2026-10-17 廃止）。
 */
export function gainExp(
  profile: Profile,
  res: Resources,
  exp: number,
  baseExp: number,
  now: number,
  dayKey: string,
): { levelUps: number; reidama: number } {
  const max = levelCapOf(profile);
  const before = levelInfo(res.exp, max).level;
  let reidama = 0;
  const cap = expForLevel(max);
  if (before >= max) {
    // すでに上限：素の分だけを霊玉へ
    reidama = convertOverflow(profile, baseExp, dayKey);
  } else {
    const room = cap - res.exp;
    if (exp > room) {
      // 上限を越えた分のうち、素の割合だけ換算
      const over = exp - room;
      const freeShare = exp > 0 ? baseExp / exp : 0;
      reidama = convertOverflow(profile, Math.floor(over * freeShare), dayKey);
    }
  }
  res.exp = Math.min(cap, res.exp + exp);
  profile.freeExp += baseExp;
  res.reidama += reidama;
  const after = levelInfo(res.exp, max).level;
  profile.level = after;
  return { levelUps: after - before, reidama };
}

function convertOverflow(profile: Profile, baseExp: number, dayKey: string): number {
  const c = CONFIG.level;
  if (profile.overflow.day !== dayKey) profile.overflow = { day: dayKey, reidama: 0 };
  const want = Math.floor(baseExp / c.overflowExpPerReidama);
  const n = Math.max(0, Math.min(want, c.overflowDailyCap - profile.overflow.reidama));
  profile.overflow.reidama += n;
  return n;
}
